import OrzioMark from "./OrzioMark";

export type LoaderVariant = "sm" | "md" | "lg" | "fullscreen";
export type LoaderContext = "default" | "estimate" | "quantities" | "report";
export type LoaderState = "estimating" | "preparing" | "loading" | "saving" | "generating";

interface OrzioLoaderProps {
  variant?: LoaderVariant;
  context?: LoaderContext;
  state?: LoaderState;
  label?: string;
  message?: string;
  progress?: number;
  showProgress?: boolean;
  showName?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const LABELS: Record<LoaderState, string> = {
  estimating: "A calcular orçamento...",
  preparing: "A preparar o projeto...",
  loading: "A carregar...",
  saving: "A guardar...",
  generating: "A gerar ficheiros...",
};

const CONTEXT_LABELS: Record<LoaderContext, string> = {
  default: LABELS.loading,
  estimate: LABELS.estimating,
  quantities: "A calcular quantidades...",
  report: LABELS.generating,
};

const MARK_SIZE: Record<LoaderVariant, "sm" | "md" | "lg" | "loading"> = {
  sm: "sm",
  md: "md",
  lg: "loading",
  fullscreen: "loading",
};

export default function OrzioLoader({
  variant = "md",
  context = "default",
  state,
  label,
  message,
  progress,
  showProgress = false,
  showName = true,
  size,
  className = "",
}: OrzioLoaderProps) {
  const displayLabel = label ?? message ?? (state ? LABELS[state] : CONTEXT_LABELS[context]);
  const markSize = size ?? MARK_SIZE[variant];
  const progressValue = Math.max(0, Math.min(100, progress ?? 68));

  if (variant === "fullscreen") {
    return (
      <div className={["orzio-loader-fullscreen", className].join(" ")} role="status" aria-live="polite" aria-label={displayLabel}>
        <div className="orzio-loader-fullscreen__panel">
          <OrzioMark size="loading" animated glow />
          <div className="text-center">
            <div className="text-2xl font-bold tracking-tight text-slate-950">Orzio</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
              Smart construction estimating
            </div>
          </div>
          <LoaderProgress label={displayLabel} progress={progressValue} showProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={["orzio-loader", `orzio-loader--${variant}`, className].join(" ")} role="status" aria-live="polite" aria-label={displayLabel}>
      <OrzioMark size={markSize} animated glow />
      {showName && (
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-950">Orzio</div>
          <LoaderProgress label={displayLabel} progress={progressValue} showProgress={showProgress} compact={variant === "sm"} />
        </div>
      )}
    </div>
  );
}

function LoaderProgress({
  label,
  progress,
  showProgress,
  compact = false,
}: {
  label: string;
  progress: number;
  showProgress: boolean;
  compact?: boolean;
}) {
  return (
    <div className={compact ? "min-w-[8rem]" : "min-w-[13rem]"}>
      <div className="flex items-center justify-between gap-4 text-xs text-slate-500">
        <span className="truncate">{label}</span>
        {showProgress && <span className="font-mono-numbers text-slate-600">{Math.round(progress)}%</span>}
      </div>
      {showProgress && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
          <div className="orzio-loader__bar h-full rounded-full" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}
